import React, { useState } from 'react';
import { X, User, Building, Award, Shield } from 'lucide-react';

interface ProfileEditDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (profile: ProfileData) => void;
    currentProfile: ProfileData;
}

export interface ProfileData {
    name: string;
    badgeNumber?: string;
    title?: string;
    agency?: string;
}

const ProfileEditDialog: React.FC<ProfileEditDialogProps> = ({
    isOpen,
    onClose,
    onSave,
    currentProfile
}) => {
    const [profile, setProfile] = useState<ProfileData>(currentProfile);

    if (!isOpen) return null;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!profile.name.trim()) return;
        onSave(profile);
        onClose();
    };

    const inputClass = "w-full bg-slate-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 text-gray-900 dark:text-gray-200 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2";
    const labelClass = "block text-xs font-medium text-gray-400 mb-1";

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl w-full max-w-md overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                        <User size={20} className="text-indigo-500" />
                        Edit Profile
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    {/* Name */}
                    <div>
                        <label htmlFor="name" className={labelClass}>Full Name *</label>
                        <div className="relative">
                            <User size={14} className="absolute left-2.5 top-3 text-gray-500" />
                            <input
                                id="name"
                                type="text"
                                required
                                value={profile.name}
                                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                                placeholder="John Doe"
                                className={`${inputClass} pl-8`}
                                autoFocus
                            />
                        </div>
                    </div>

                    {/* Badge Number */}
                    <div>
                        <label htmlFor="badge" className={labelClass}>Badge Number</label>
                        <div className="relative">
                            <Shield size={14} className="absolute left-2.5 top-3 text-gray-500" />
                            <input
                                id="badge"
                                type="text"
                                value={profile.badgeNumber || ''}
                                onChange={(e) => setProfile({ ...profile, badgeNumber: e.target.value })}
                                placeholder="12345"
                                className={`${inputClass} pl-8`}
                            />
                        </div>
                    </div>

                    {/* Title */}
                    <div>
                        <label htmlFor="title" className={labelClass}>Title / Rank</label>
                        <div className="relative">
                            <Award size={14} className="absolute left-2.5 top-3 text-gray-500" />
                            <input
                                id="title"
                                type="text"
                                value={profile.title || ''}
                                onChange={(e) => setProfile({ ...profile, title: e.target.value })}
                                placeholder="Detective"
                                className={`${inputClass} pl-8`}
                            />
                        </div>
                    </div>

                    {/* Agency */}
                    <div>
                        <label htmlFor="agency" className={labelClass}>Agency / Department</label>
                        <div className="relative">
                            <Building size={14} className="absolute left-2.5 top-3 text-gray-500" />
                            <input
                                id="agency"
                                type="text"
                                value={profile.agency || ''}
                                onChange={(e) => setProfile({ ...profile, agency: e.target.value })}
                                placeholder="Metropolitan Police Department"
                                className={`${inputClass} pl-8`}
                            />
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-slate-700 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-md transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors shadow-sm"
                        >
                            Save Profile
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProfileEditDialog;
